import { navigate } from 'gatsby';
import React, { FC } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import SubheadingText from '@ui/atoms/SubheadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import FlexColToRow from '@ui/molecules/FlexColToRow';

import { IFlowStepProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme';

export interface IIneligibleExceptionsStepPageContent {
	pageHeadline: string;
	liverDiseaseHeader: string;
	liverDiseaseBody: string;
	hormoneCancerHeader: string;
	hormoneCancerBody: string;
	prolactinHeader: string;
	prolactinBody: string;
	vcCtaHeader: string;
	vcCtaBody: string;
	backButtonLabel: string;
	vcCtaLabel: string;
	returnToFolxLabel: string;
}

interface IIneligibleExceptionsStepProps extends IFlowStepProps {
	pageContent: IIneligibleExceptionsStepPageContent;
	handleNavBack: (stepKey: string) => void;
}

const TopSection = styled(CenteredGridItem)`
	> ${HeadingText} {
		margin-bottom: ${theme.spacing.sm};
	}
`;

const ParagraphContainer = styled(FlexColumn)`
	margin-top: ${theme.spacing.lgxl};

	${SubheadingText} {
		margin-bottom: ${theme.spacing.sm};
	}
`;

const BottomSection = styled(FlexColumn)`
	margin-top: ${theme.spacing.lgxl};
	border-top: solid 1px ${theme.colors.black};
	padding: ${theme.spacing.lg} 0;

	${SubheadingText} {
		margin-bottom: ${theme.spacing.sm};
	}
`;

const MainButtonSection = styled(FlexColToRow).attrs({ breakpoint: 'tablet' })`
	display: flex;
	justify-content: space-between;
	margin-bottom: ${theme.spacing.lgxl};
	align-items: center;
	gap: ${theme.spacing.sm};
	@media ${theme.responsive.breakpoints.tablet} {
		${Button}:first-child {
			max-width: 15rem;
			height: 100%;
		}
	}
`;

const PageFooter = styled(FlexColumn)`
	padding-top: ${theme.spacing.lg};
	margin-bottom: ${theme.spacing.xxxl};
	border-top: solid 1px ${theme.colors.black};
	align-items: center;

	${Button} {
		max-width: 31.1rem;
	}
`;

const IneligibleExceptionsStep: FC<IIneligibleExceptionsStepProps> = ({
	stepKey,
	pageContent,
	handleNavBack,
}) => {
	const {
		pageHeadline,
		liverDiseaseHeader,
		liverDiseaseBody,
		hormoneCancerHeader,
		hormoneCancerBody,
		prolactinHeader,
		prolactinBody,
		vcCtaHeader,
		vcCtaBody,
		backButtonLabel,
		vcCtaLabel,
		returnToFolxLabel,
	} = pageContent;

	return (
		<>
			<TopSection>
				<HeadingText>{pageHeadline}</HeadingText>
			</TopSection>
			<CenteredGridItem>
				<ParagraphContainer>
					<WYSIWYGContent dangerouslySetInnerHTML={{ __html: liverDiseaseBody }} />
				</ParagraphContainer>
				<ParagraphContainer>
					<SubheadingText>{hormoneCancerHeader}</SubheadingText>
					<WYSIWYGContent dangerouslySetInnerHTML={{ __html: hormoneCancerBody }} />
				</ParagraphContainer>
				<ParagraphContainer></ParagraphContainer>
				<MainButtonSection>
					<Button
						variant="secondary"
						label={backButtonLabel}
						onClick={() => navigate('/app/flows/mh')}
					/>
				</MainButtonSection>
				<PageFooter>
					<Button variant="secondary" label={returnToFolxLabel} onClick={() => navigate('/')} />
				</PageFooter>
			</CenteredGridItem>
		</>
	);
};

export default IneligibleExceptionsStep;
